import { Component, forwardRef, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectorValueDirective } from './selector-value.directive';

@Component({
  selector: 'app-multiselector',
  templateUrl: './multi-selector.component.html',
  styleUrls: ['./multi-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectorComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class MultiSelectorComponent implements ControlValueAccessor, OnInit {
  proposedValues: object[];
  selection: object[] = [];
  isDropDownVisible = false;
  private rawSelection: any[];

  constructor() {}
  hasFocus: boolean;
  @Input() valueIdProperty = 'id';
  @Input() valueCaptionProperty = 'libelle';
  @Input() placeholder: string;
  @Input() valueIsDeletableProperty;
  @Input() detailedDeletionMode = true
  @Input() isError;
  @Input() formControl;
  @Input() proposedValuesFilter: (item: any) => boolean;
  private allValues: object[];

  @Input() set items(values: object[]) {
    this.allValues = values ?? [];
    this.proposedValues = this.proposedValuesFilter
      ? this.allValues.filter(this.proposedValuesFilter)
      : [...this.allValues];

    if (this.rawSelection != null) {
      this.selection = this.findItemsByIds(this.rawSelection);
      this.removeSelectionFromProposedValues();
      this.rawSelection = null;
    } else if (this.selection.length > 0) {
      this.selection = this.findItemsByIds(this.selection.map(item => this.getIdentifier(item)));
      this.removeSelectionFromProposedValues();
    }
  }

  ngOnInit(): void {}

  private onChange: (value) => any = v => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  private saveSelectionWhileItemListIsStillLoading(values: any[]) {
    this.rawSelection = values;
  }

  writeValue(values: any[]): void {
    if (values == null) {
      values = [];
    }
    if (this.allValues != null && this.allValues.length > 0) {
      this.selection = this.findItemsByIds(values);
      this.removeSelectionFromProposedValues();
    } else {
      this.saveSelectionWhileItemListIsStillLoading(values);
      this.selection = [];
    }
  }

  private findItemsByIds(ids: any[]): object[] {
    if (!ids?.length || !this.allValues?.length) {
      return [];
    }
    return ids
      .map(id => this.allValues.find(item => this.sameId(this.getIdentifier(item), id)))
      .filter((item): item is object => item != null);
  }

  private sameId(left: any, right: any): boolean {
    return left != null && right != null && String(left) === String(right);
  }

  private isSelected(item: object): boolean {
    const itemId = this.getIdentifier(item);
    return this.selection.some(selected => this.sameId(this.getIdentifier(selected), itemId));
  }

  private removeSelectionFromProposedValues() {
    if (this.proposedValues != null) {
      this.proposedValues = this.proposedValues.filter(item => !this.isSelected(item));
      if (this.proposedValuesFilter) {
        this.proposedValues = this.proposedValues.filter(this.proposedValuesFilter);
      }
    }
  }

  getIdentifier(item: object) {
    if (item != null) {
      return item[this.valueIdProperty];
    }
  }

  getCaption(item: object) {
    if (item != null) {
      return item[this.valueCaptionProperty];
    }
  }

  isDeletable(item: any) {
    if (item !== null) {
      if (!this.detailedDeletionMode) {
        return true;
      }
      if (item.isGerableUtilisateur !== undefined) {
        return item.isGerableUtilisateur === true;
      }
      return item.editable || !this.detailedDeletionMode;
    } else {
      return true;
    }
  }

  isSelectionEmpty() {
    return this.selection == null || this.selection.length == 0;
  }

  addItemToSelection(item: object) {
    if (!this.isSelected(item)) {
      this.selection = [...this.selection, item];
      this.proposedValues = this.proposedValues.filter(proposed => !this.sameId(this.getIdentifier(proposed), this.getIdentifier(item)));
      this.closeDropDown();
      this.notifyValueChange();
    }
  }

  private notifyValueChange() {
    this.onChange(this.selection.map(it => this.getIdentifier(it)));
  }

  closeDropDown() {
    this.isDropDownVisible = false;
  }

  toggleDropDown() {
    this.isDropDownVisible = !this.isDropDownVisible;
  }

  supprimer(item: SelectorValueDirective, $event) {
    $event.stopPropagation();
    const itemId = this.getIdentifier(item);
    this.selection = this.selection.filter(selected => !this.sameId(this.getIdentifier(selected), itemId));
    let filtered = this.allValues.filter(it => !this.isSelected(it));
    if (this.proposedValuesFilter) {
      filtered = filtered.filter(this.proposedValuesFilter);
    }
    this.proposedValues = filtered;
    this.notifyValueChange();
  }

  onFocusLost() {
    this.hasFocus = false;
    this.closeDropDown();
  }
}
